<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import type { QuotaDocument } from "@kekkle/shared/types/documents/quota-document/quota-document";

const { t } = useI18n();

const isOpen = defineModel<boolean>("open", { required: true });

type Props = {
  quota: QuotaDocument | null;
  loading: boolean;
  title?: string;
  emptyText?: string;
  deleteInfoText?: string;
};

const props = defineProps<Props>();

type ProgressItem = {
  label: string;
  count: number;
  limit: number;
  color: string;
  warningColor: string;
};

const progressItems = computed<ProgressItem[]>(() => {
  if (!props.quota) return [];
  return [
    {
      label: t("pages.setting.quota.daily"),
      count: props.quota.daily_count,
      limit: props.quota.daily_limit,
      color: "bg-kekkle-purple",
      warningColor: "bg-kekkle-red",
    },
    {
      label: t("pages.setting.quota.weekly"),
      count: props.quota.weekly_count,
      limit: props.quota.weekly_limit,
      color: "bg-kekkle-blue",
      warningColor: "bg-kekkle-red",
    },
    {
      label: t("pages.setting.quota.total"),
      count: props.quota.total_count,
      limit: props.quota.total_limit,
      color: "bg-kekkle-yellow",
      warningColor: "bg-kekkle-red",
    },
  ];
});

function percentage(count: number, limit: number): number {
  if (limit === 0) return 100;
  return Math.min((count / limit) * 100, 100);
}

function isNearLimit(count: number, limit: number): boolean {
  return count >= limit * 0.8;
}
</script>

<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="purple"
    padding="normal"
    initial-height="half"
  >
    <div class="dark:text-slate-200">
      <div class="mb-4 flex items-center justify-between">
        <h2 class="text-xl font-bold">
          {{ props.title || t("pages.setting.quota.title") }}
        </h2>
        <span
          v-if="quota?.tier"
          class="rounded-full border border-black-dark bg-kekkle-yellow px-2.5 py-0.5 text-xs font-black uppercase text-black-dark shadow-sm"
        >
          {{ quota.tier }}
        </span>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="space-y-4">
        <div
          v-for="i in 3"
          :key="i"
          class="h-16 animate-pulse rounded-lg bg-gray-200 dark:bg-kekkle-dark-light"
        />
      </div>

      <!-- No quota doc yet -->
      <div
        v-else-if="!quota"
        class="py-4 text-center text-gray-500 dark:text-slate-400"
      >
        {{ props.emptyText || t("pages.setting.quota.no_usage") }}
      </div>

      <!-- Progress bars -->
      <div v-else class="space-y-4">
        <div
          v-for="item in progressItems"
          :key="item.label"
          class="rounded-lg border-2 border-black-dark bg-white p-3 dark:bg-kekkle-dark-light"
        >
          <div class="mb-2 flex items-center justify-between">
            <span class="text-sm font-bold">{{ item.label }}</span>
            <span
              class="text-sm font-bold"
              :class="
                isNearLimit(item.count, item.limit)
                  ? 'text-kekkle-red'
                  : 'text-gray-600 dark:text-slate-400'
              "
            >
              {{ item.count }} / {{ item.limit }}
            </span>
          </div>
          <div
            class="h-3 w-full overflow-hidden rounded-full border border-black-dark bg-gray-100 dark:bg-kekkle-dark"
          >
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="
                isNearLimit(item.count, item.limit)
                  ? item.warningColor
                  : item.color
              "
              :style="{ width: `${percentage(item.count, item.limit)}%` }"
            />
          </div>
        </div>

        <!-- Info text -->
        <p class="mt-2 text-xs text-gray-500 dark:text-slate-400">
          {{ t("pages.setting.quota.reset_info") }}
        </p>
        <p class="mt-1 text-xs text-gray-500 dark:text-slate-400">
          {{ props.deleteInfoText || t("pages.setting.quota.delete_info") }}
        </p>
      </div>
    </div>
  </KekkleBottomSheet>
</template>
